import { useEffect, useRef, useState, useCallback } from 'react';
import { updateTypingStatus } from '../services/api';

/**
 * Hook to manage typing indicators in conversations
 * Handles debounced typing status updates and listening to other users' typing
 */
export const useTypingIndicator = (conversationId, echo) => {
  const [usersTyping, setUsersTyping] = useState(new Set());
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const stopTypingTimeoutRef = useRef(null);

  // Send typing status to server
  const sendTypingStatus = async (isTyping) => {
    if (!conversationId) {
      return;
    }

    try {
      const response = await updateTypingStatus(conversationId, isTyping);
    } catch (error) {
      console.error('Error details:', error.response?.data);
    }
  };

  // Handle user starting to type
  const handleTypingStart = useCallback(() => {
    if (!conversationId) return;

    // Clear any existing "stop typing" timeout
    if (stopTypingTimeoutRef.current) {
      clearTimeout(stopTypingTimeoutRef.current);
      stopTypingTimeoutRef.current = null;
    }

    // If not already typing, send typing status
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      sendTypingStatus(true);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      handleTypingStop();
    }, 1000);
  }, [conversationId]);

  // Handle user stopping typing
  const handleTypingStop = useCallback(() => {
    if (!conversationId) return;

    if (isTypingRef.current) {
      isTypingRef.current = false;
      sendTypingStatus(false);
    }

    // Clear timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [conversationId]);

  // Debounced typing handler for input changes
  const onTyping = useCallback((e) => {
    // Only trigger if there's actual content
    if (e?.target?.value?.trim()) {
      handleTypingStart();
    } else {
      handleTypingStop();
    }
  }, [handleTypingStart, handleTypingStop]);

  // Listen to typing events from other users
  useEffect(() => {
    if (!echo || !conversationId) {
      return;
    }

    // Check if echo has required methods
    if (!echo.private || typeof echo.private !== 'function') {
      console.error('❌ Echo instance does not have .private() method:', echo);
      console.error('Available methods:', Object.keys(echo));
      return;
    }

    const channelName = `conversation.${conversationId}`;

    try {
      // Subscribe to the conversation channel
      const channel = echo.private(channelName);

      // Listen for user typing event
      channel.listen('.user.typing', (data) => {
        setUsersTyping(prev => {
          const newSet = new Set(prev);
          newSet.add(data.user_id);
          return newSet;
        });

        // Auto-remove after 5 seconds if no stopped typing event
        setTimeout(() => {
          setUsersTyping(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.user_id);
            return newSet;
          });
        }, 1000);
      });

      // Listen for user stopped typing event
      channel.listen('.user.stopped-typing', (data) => {
        setUsersTyping(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.user_id);
          return newSet;
        });
      });

      return () => {
        channel.stopListening('.user.typing');
        channel.stopListening('.user.stopped-typing');
      };
    } catch (error) {
      console.error('Error setting up typing listeners:', error);
    }
  }, [echo, conversationId]);

  // Cleanup on unmount or conversation change
  useEffect(() => {
    return () => {
      handleTypingStop();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (stopTypingTimeoutRef.current) {
        clearTimeout(stopTypingTimeoutRef.current);
      }
    };
  }, [conversationId]);

  return {
    usersTyping: Array.from(usersTyping),
    onTyping,
    isTyping: isTypingRef.current,
  };
};

